import { useMemo } from 'react';
import { AccommodationListingDTO, SortOption, Filters, JoyHotelAccommodationListingDTO } from '../AccommodationsConsole.types';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useAccommodationsSortOrder } from './useAccommodationsSortOrder';
import { sortByFavorite, sortByOrderList } from '@shared/utils/array';
import { useListingsSortQueryParam } from './useAccommodationsConsoleSearchParamsState/useListingsSortQueryParam';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';

/** Not Room Block - Standard Hotel Helper function to check if a listing is a standard hotel.*/
function isStandardHotel(listing: AccommodationListingDTO): listing is JoyHotelAccommodationListingDTO {
  return listing.type === 'standardHotel';
}

export const useListingSort = (listings: ReadonlyArray<AccommodationListingDTO>, venue?: Maybe<{ name: string; location: Maybe<LatLngLiteral> }>, filters?: Filters) => {
  const { pageContainerBySlug } = useAccommodationsSortOrder();

  const { currentSort, updateSort } = useListingsSortQueryParam();

  const sortedListings = useMemo(() => {
    const listingsCopy = [...listings];
    let sorted: AccommodationListingDTO[];

    switch (currentSort) {
      case SortOption.TOP_MATCHES:
        sorted = listingsCopy.sort((a, b) => (b.searchScore ?? 0) - (a.searchScore ?? 0));
        break;
      case SortOption.COUPLES_PICKS: {
        const blockOrdering = pageContainerBySlug?.blockOrdering;
        const coupleListings = listingsCopy.filter(listing => listing.isPickedByCouple);
        const nonCoupleListings = listingsCopy.filter(listing => !listing.isPickedByCouple);
        const sortedCoupleListings = sortByFavorite(sortByOrderList(coupleListings, blockOrdering ?? []));
        sorted = [...sortedCoupleListings, ...nonCoupleListings];
        break;
      }

      case SortOption.PRICE_LOW_TO_HIGH:
        sorted = listingsCopy.sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          return aPrice - bPrice;
        });
        break;

      case SortOption.PRICE_HIGH_TO_LOW:
        sorted = listingsCopy.sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? -1;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? -1;
          return bPrice - aPrice;
        });
        break;

      case SortOption.MOST_SAVINGS:
        sorted = listingsCopy.sort((a, b) => {
          const aDiscount = a.pricingInfo?.values?.discountPercentage ?? 0;
          const bDiscount = b.pricingInfo?.values?.discountPercentage ?? 0;
          return bDiscount - aDiscount;
        });
        break;

      case SortOption.DISTANCE:
        sorted = listingsCopy.sort((a, b) => {
          if (venue?.location) {
            const aDistance = distanceInMiles(venue.location, a.location) ?? Number.MAX_VALUE;
            const bDistance = distanceInMiles(venue.location, b.location) ?? Number.MAX_VALUE;
            return aDistance - bDistance;
          }
          return 0;
        });
        break;

      default:
        sorted = listingsCopy;
    }

    // When showAvailableOnly is true, move sold-out hotels to the end
    if (filters?.showAvailableOnly) {
      const { available, others } = sorted.reduce(
        (acc, listing) => {
          if (isStandardHotel(listing) && listing.isSoldOutForSelectedDates) {
            acc.others.push(listing);
          } else {
            acc.available.push(listing);
          }
          return acc;
        },
        { available: [] as AccommodationListingDTO[], others: [] as AccommodationListingDTO[] },
      );
      return [...available, ...others];
    }

    return sorted;
  }, [listings, pageContainerBySlug?.blockOrdering, currentSort, venue, filters?.showAvailableOnly]);

  return {
    sortedListings,
    sortOption: currentSort,
    updateSort,
  };
};
